import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { setDialog } from '../../store/actions/dialog'
import {
  Box,
  TableFooter,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  DialogTitle,
  DialogContent,
  Dialog,
  DialogActions
} from '@material-ui/core'
import { ArrowDropUp, ArrowDropDown } from '@material-ui/icons'
import CenteredSubmitButton from '../molecules/CenteredSubmitButton'
import { Category } from '../../types/Category'
import { Notification } from '../../types/Notification'

const InputCategory: React.FC<
  ReturnType<typeof mapDispatchToProps> & {
    isOpen: boolean
    handleClose: () => void
    handleRefresh: () => Promise<void>
  }
> = ({ isOpen, handleClose, setDialog }) => {
  const [categories, setCategories] = useState<Category[]>([])

  const fetchNotification = async (): Promise<{ number_of_hits: number; rows: Notification[] }> => {
    const { data } = await axios.get<{ number_of_hits: number; rows: Notification[] }>(
      '/notifications'
    )
    return data
  }

  const fetchCategories = async (): Promise<{ number_of_hits: number; rows: Category[] }> => {
    const { data } = await axios.get<{ number_of_hits: number; rows: Category[] }>(
      `notifications/category`,
      {
        params: {
          sort: ['sort'],
          order: ['asc']
        }
      }
    )
    return data
  }

  const fetchData = async () => {
    try {
      const data = await fetchCategories()
      console.log('rows', data.rows)
      setCategories(
        data.rows.length > 0
          ? data.rows
          : [
              {
                id: null,
                category: '',
                member_flg: 0,
                sort: 0,
                created_at: null,
                modified_at: null
              }
            ]
      )
    } catch (error) {
      alert(error.message)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const submitCheck = async () => {
    if (categories.length > 0) {
      const dbCategory = await fetchCategories()
      const notification = await fetchNotification()
      const errors = categories.reduce((errors, category, index) => {
        // 会員限定フラグ変更時のプッシュ通知予約存在チェック
        const changedMemberFlg = (): Boolean => {
          if (
            dbCategory.rows.some(c => c.member_flg !== category.member_flg && c.id === category.id)
          ) {
            return notification.rows.some(
              d =>
                d.push_reserved_date &&
                !d.push_at &&
                d.category === category.id &&
                d.push_member_flg !== category.member_flg
            )
          } else {
            return false
          }
        }
        if (!category.category) {
          return [...errors, 'カテゴリ名が未入力です。']
        } else if (
          categories.some((c, cIndex) => c.category === category.category && cIndex !== index)
        ) {
          return [...errors, 'カテゴリ名が重複しています。']
        } else if (changedMemberFlg()) {
          return [
            ...errors,
            '会員限定フラグを変更できません。お知らせのプッシュ通知送信予約を取り消してください。'
          ]
        } else {
          return errors
        }
      }, [])
      return errors.filter((x, i) => errors.indexOf(x) === i)
    } else {
      return []
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault()
      const errors = await submitCheck()
      if (errors.length > 0) {
        alert(errors.join('\n'))
      } else {
        await axios.post(`/notifications/category`, { categories })
        fetchData()
        handleClose()
      }
    } catch (err) {
      alert(err.message)
    }
  }

  const handleChangeCategory = (
    index: number,
    key: keyof Category,
    value: Category[keyof Category]
  ) => {
    const targets = [...categories]
    targets.splice(index, 1, { ...targets[index], [key]: value })
    setCategories(targets)
  }

  const handleChangeSort = async (sourceIndex: number, destIndex: number) => {
    const targets = [...categories]
    const source = { ...targets[sourceIndex] }
    const dest = { ...targets[destIndex] }
    if (sourceIndex > destIndex) {
      // 上へ
      targets.splice(destIndex, 2, source, dest)
    } else {
      // 下へ
      targets.splice(sourceIndex, 2, dest, source)
    }
    setCategories(targets)
  }

  const handleRemove = async (index: number) => {
    // プッシュ通知予約存在チェック
    const category = categories.filter((c, i) => i == index && c.id !== null)
    const notification = await fetchNotification()
    if (
      notification.rows.some(
        d => d.push_reserved_date && !d.push_at && d.category === category[0].id
      )
    ) {
      alert('カテゴリを削除できません。お知らせのプッシュ通知送信予約を取り消してください。')
      return
    }

    setDialog(
      '確認',
      '削除してもよろしいですか？',
      () => {
        setCategories(categories.filter((_, i) => i !== index))
      },
      () => {}
    )
  }

  const handleAdd = () => {
    setCategories([
      ...categories,
      { id: null, category: '', member_flg: 0, sort: 0, created_at: null, modified_at: null }
    ])
  }

  return (
    <React.Fragment>
      <Dialog scroll="paper" open={isOpen} onClose={handleClose}>
        <DialogTitle id="confirmation-dialog-title">
          <Typography variant="subtitle2" paragraph>
            カテゴリを編集
          </Typography>
          <Typography variant="body2" paragraph>
            <div>お知らせをカテゴリ別に登録することができます。</div>
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Card>
            <CardContent>
              <Table size="small" padding="default">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">カテゴリ名</TableCell>
                    <TableCell align="center">会員限定</TableCell>
                    <TableCell align="center">並び替え</TableCell>
                    <TableCell align="center">削除</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((category, index) => (
                    <TableRow key={index}>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" alignItems="center">
                          <TextField
                            value={category.category}
                            onChange={e => {
                              handleChangeCategory(index, 'category', e.target.value)
                            }}
                            margin="dense"
                            variant="outlined"
                            placeholder="カテゴリ名を入力"
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="subtitle2">
                          <FormControlLabel
                            value="top"
                            control={
                              <Checkbox
                                checked={category.member_flg === 1}
                                onChange={() => {
                                  if (category.member_flg === 1) {
                                    handleChangeCategory(index, 'member_flg', 0)
                                  } else {
                                    handleChangeCategory(index, 'member_flg', 1)
                                  }
                                }}
                                value="only-unread-flg"
                              />
                            }
                            label={category.member_flg === 1 ? 'ON' : 'OFF'}
                            labelPlacement="end"
                          />
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box width={70} height={100} mx="auto">
                          <Box
                            display="flex"
                            height={100}
                            flexDirection="column"
                            justifyContent="space-around"
                          >
                            <Button
                              disabled={index === 0}
                              variant="contained"
                              size="small"
                              color="primary"
                              onClick={() => {
                                handleChangeSort(index, index - 1)
                              }}
                            >
                              <ArrowDropUp />
                            </Button>
                            <Button
                              disabled={categories.length - 1 === index}
                              variant="contained"
                              size="small"
                              color="primary"
                              onClick={() => {
                                handleChangeSort(index, index + 1)
                              }}
                            >
                              <ArrowDropDown />
                            </Button>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            handleRemove(index)
                          }}
                        >
                          削除
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableCell colSpan={4}>
                    <Box display="flex" justifyContent="flex-end">
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          handleAdd()
                        }}
                      >
                        追加
                      </Button>
                    </Box>
                  </TableCell>
                </TableFooter>
              </Table>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions style={{ display: 'flex', justifyContent: 'center' }}>
          <form onSubmit={event => handleSubmit(event)}>
            <CenteredSubmitButton
              submittable={true}
              resettable
              resetText="キャンセル"
              submitHandler={() => {}}
              resetHandler={handleClose}
              size="large"
            />
          </form>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    setDialog: (
      title: string,
      content: string,
      okHandler: () => void,
      ngHandler: () => void,
      noCancel?: boolean
    ) => dispatch(setDialog(title, content, okHandler, ngHandler, noCancel || false))
  }
}

export default connect(null, mapDispatchToProps)(InputCategory)
